import React, { useState } from 'react';
import { Box, Typography, styled, Chip, IconButton, Tooltip } from '@mui/material';
import { useHistory } from 'react-router-dom';
import Book from 'src/widgets/Menu/Icons/Book';
import MapLayer from 'src/widgets/Menu/Icons/MapLayer';
import Chats from 'src/widgets/Menu/Icons/Chats';
import TrashIcon from 'src/icons/TrashIcon';
import { IProjectResponse } from 'src/types/Projects';
import { ProjectWithSubscription } from 'src/types/Subscription';
import MenuButton from 'src/widgets/Menu/components/MenuButton';
import CustomSwitch from 'src/components/CustomSwitch';
import DeleteProjectDialog from 'src/components/DeleteProjectDialog';
import { ROUTES_URL } from 'src/constants/routes';

interface ProjectCardProps {
    project: IProjectResponse | ProjectWithSubscription;
    onClick: (project: IProjectResponse | ProjectWithSubscription) => void;
    onToggleActive: (projectId: number, isActive: boolean) => void;
    onDelete?: (projectId: number) => Promise<void>;
    activeProjectsCount?: number;
    projectsLimit?: number;
    hasActiveSubscription?: boolean;
}

const Container = styled(Box)(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: 'var(--lm-surface)',
    borderRadius: 12,
    border: '1px solid var(--lm-border)',
}));

const ProjectHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
}));

const ProjectName = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    color: theme.palette.text.primary,
    fontSize: '1.125rem', // 18px
}));

const MenuButtonsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
}));

const ProjectCard: React.FC<ProjectCardProps> = ({
    project,
    onClick,
    onToggleActive,
    onDelete,
    activeProjectsCount = 0,
    projectsLimit = 0,
    hasActiveSubscription = false,
}) => {
    const history = useHistory();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Получаем данные из проекта
    const keywordsCount = 'keywords' in project ? project.keywords?.length || 0 : 0;

    // Количество чатов из личной подборки проекта
    const privateBundle = 'bundles' in project ? project.bundles.find((bundle) => !bundle.is_public) : null;
    const chatsCount = privateBundle?.chat_count || 0;

    // Количество публичных подборок проекта
    const publicBundles = 'bundles' in project ? project.bundles.filter((bundle) => bundle.is_public) : [];
    const bundlesCount = publicBundles.length;

    const hasSubscription = 'subscription' in project && project.subscription;

    const handleClick = () => {
        onClick(project);
    };

    const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation(); // Предотвращаем клик по карточке

        // Блокируем активацию, если нет тарифа или лимит достигнут
        if (event.target.checked && !project.is_active) {
            if (!hasActiveSubscription) {
                return; // Не вызываем onToggleActive, свитчер останется в исходном положении
            }
            if (activeProjectsCount >= projectsLimit && projectsLimit > 0) {
                return; // Не вызываем onToggleActive, свитчер останется в исходном положении
            }
        }

        onToggleActive(project.id, event.target.checked);
    };

    // Определяем, должен ли свитчер быть заблокирован
    const isSwitchDisabled =
        !project.is_active && (!hasActiveSubscription || (activeProjectsCount >= projectsLimit && projectsLimit > 0));

    const handleKeywordsClick = () => {
        history.push(`${ROUTES_URL.KEYWORDS}?projectId=${project.id}`);
    };

    const handleChatsClick = () => {
        history.push(`${ROUTES_URL.CHATS_AND_CHANNELS}?projectId=${project.id}`);
    };

    const handleCollectionsClick = () => {
        history.push(`${ROUTES_URL.COLLECTIONS}?projectId=${project.id}`);
    };

    const handleAboutClick = () => {
        history.push(`${ROUTES_URL.ABOUT_PROJECT}?projectId=${project.id}`);
    };

    const handleDeleteClick = (event: React.MouseEvent) => {
        event.stopPropagation(); // Предотвращаем клик по карточке
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!onDelete) {
            console.error('onDelete не передан в ProjectCard');
            return;
        }

        setIsDeleting(true);
        try {
            console.log('Начинаем удаление проекта:', project.id);
            await onDelete(project.id);
            console.log('Проект успешно удален');
            setDeleteDialogOpen(false);
        } catch (error) {
            console.error('Ошибка удаления проекта:', error);
            // Не закрываем модальное окно при ошибке, чтобы пользователь мог повторить попытку
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <Container>
                <ProjectHeader>
                    <ProjectName>{project.name}</ProjectName>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        <IconButton
                            size="medium"
                            onClick={handleDeleteClick}
                            disabled={isDeleting}
                            sx={{
                                padding: 0.5,
                                color: 'inherit',
                                '&:hover': {
                                    color: 'rgba(228,63,63,0.8)',
                                },
                            }}
                        >
                            <TrashIcon />
                        </IconButton>
                        {isSwitchDisabled ? (
                            <Tooltip
                                title={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ mb: 0.5 }}
                                        >
                                            {!hasActiveSubscription
                                                ? 'Для активации проекта необходимо выбрать тариф'
                                                : `Достигнут лимит активных проектов (${projectsLimit})`}
                                        </Typography>
                                        {hasActiveSubscription && (
                                            <Typography variant="body2">
                                                Сначала деактивируйте один из активных проектов или обновите тариф для
                                                увеличения лимита
                                            </Typography>
                                        )}
                                    </Box>
                                }
                                arrow
                                placement="top"
                            >
                                <span>
                                    <CustomSwitch
                                        checked={project.is_active}
                                        onChange={handleSwitchChange}
                                        disabled={isSwitchDisabled}
                                    />
                                </span>
                            </Tooltip>
                        ) : (
                            <CustomSwitch
                                checked={project.is_active}
                                onChange={handleSwitchChange}
                                disabled={isSwitchDisabled}
                            />
                        )}
                    </Box>
                </ProjectHeader>

                {/* Информация о тарифе */}
                {hasSubscription && project.subscription?.plan.color_code && (
                    <Box mb={1}>
                        <Chip
                            label={project.subscription?.plan.name}
                            size="small"
                            color="primary"
                            style={{
                                backgroundColor: `#${project.subscription.plan.color_code}`,
                                color: 'var(--lm-accent-contrast)',
                            }}
                        />
                    </Box>
                )}

                <MenuButtonsContainer>
                    <Box
                        display="flex"
                        gap={1}
                        width="100%"
                    >
                        <MenuButton
                            label="Словарь"
                            count={keywordsCount}
                            icon={<Book />}
                            onClick={handleKeywordsClick}
                            disabled={false}
                            inRow={true}
                        />
                        <MenuButton
                            label="Чаты"
                            count={chatsCount}
                            icon={<Chats />}
                            onClick={handleChatsClick}
                            disabled={false}
                            inRow={true}
                        />
                        <MenuButton
                            label="Подборки"
                            count={bundlesCount}
                            icon={<MapLayer />}
                            onClick={handleCollectionsClick}
                            disabled={false}
                            inRow={true}
                        />
                    </Box>
                    <MenuButton
                        label="О проекте"
                        onClick={handleAboutClick}
                        disabled={false}
                        fullWidth={true}
                    />
                </MenuButtonsContainer>
            </Container>
            <DeleteProjectDialog
                open={deleteDialogOpen}
                projectName={project.name}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleDeleteConfirm}
                isLoading={isDeleting}
            />
        </>
    );
};

export default ProjectCard;
